import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import i18n from "@/configs/i18next";
import { NAMESPACES } from "@/constants/i18next.constants";
import { isDevelopment } from "@/constants/isDevelopement";
import { useAuthStore } from "@/features/Auth";

import { TErrorCause } from "./app/app.types";
import { METHOD_EXECUTION_FAIL, UNAUTHORIZED_STATUS } from "./services.constants";

export const insertTokenInterceptor = (req: InternalAxiosRequestConfig) => {
  const { token } = useAuthStore.getState();

  if (token) {
    req.headers.set("Authorization", `Basic ${token}`);
  }

  return req;
};

export const insertAcceptLanguageInterceptor = (
  req: InternalAxiosRequestConfig,
) => {
  req.headers.set("Accept-Language", i18n.language);

  return req;
};

export const responseSuccessHandler = <T, D>({ data }: AxiosResponse<T, D>) => {
  const parsed = data as unknown as { result?: T };
  if (parsed.result) return parsed.result as T;
  return parsed as T;
};

export const responseErrorHandler = async <T, D>(err: AxiosError<T, D>) => {
  // eslint-disable-next-line no-console
  console.assert(isDevelopment, "request", err.request);
  // eslint-disable-next-line no-console
  console.assert(isDevelopment, "response", err.response);

  if (!err.response) {
    throw new Error(i18n.t("error", {
      ns: NAMESPACES.COMMON,
    }));
  }

  if (err && err.response && err.response.status >= 500) {
    throw new Error(i18n.t("serverError", {
      ns: NAMESPACES.COMMON,
    }));
  }

  const { response } = err;

  if (response.status === UNAUTHORIZED_STATUS) {
    const { logout } = useAuthStore.getState();
    logout();
  }

  const { data } = response as {
    data?: [{ message: string; cause: string; code: string }];
  };

  if (data?.[0]?.code === METHOD_EXECUTION_FAIL) {
    const cause = JSON.parse(
      data?.[0].cause.replaceAll("/\\/g", "").replace("Error:", ""),
    ) as TErrorCause;
    throw new Error(`${cause.id ? `${cause.id} - ` : ""}${cause.message}`);
  }

  if (data?.[0]?.cause || data?.[0]?.message) {
    const message = data[0].cause?.split(" - Message: ")?.[1];

    if (message) {
      throw new Error(message);
    }

    throw new Error(data?.[0]?.message);
  }
};
